import { Box, Grid, IconButton, Theme, Tooltip } from '@mui/material';
import { ExitToApp, Fullscreen } from '@mui/icons-material';
import React, { ReactNode, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@mui/styles';

const useStyles = makeStyles((theme: Theme) => ({
  fullScreen: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    zIndex: theme.zIndex.modal,
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    backgroundColor: '#fff',
    padding: '5px',
  },
  exitFullScreen: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
  },
  fsBtnStyles: {
    position: 'absolute',
    right: 0,
    padding: '5px',
  },
}));

enum KeyCodes {
  KEY_F = 'KeyF',
  KEY_ESCAPE = 'Escape',
  KEY_CONTROL = 'Control',
}

type ToggleFullScreenRender = (
  button: ReactNode,
  isExpanded: boolean,
) => ReactNode;

const ToggleFullScreen = ({
  children,
  fullScreenBtnClass,
  ignoreFullScreen = false,
  persistKey,
}: {
  children: ReactNode | ToggleFullScreenRender;
  fullScreenBtnClass?: string;
  ignoreFullScreen?: boolean;
  persistKey?: string;
}) => {
  const [isExpanded, setIsExpanded] = useState<boolean>(() => {
    if (!persistKey) {
      return false;
    }
    try {
      return window.localStorage.getItem(persistKey) === 'true';
    } catch {
      return false;
    }
  });
  const classes = useStyles();
  const { t } = useTranslation();

  useEffect(() => {
    if (!persistKey) {
      return;
    }
    try {
      window.localStorage.setItem(persistKey, String(isExpanded));
    } catch {
      // Ignore storage errors to avoid blocking UI state changes.
    }
  }, [isExpanded, persistKey]);

  useEffect(() => {
    // Let child grids re-measure when the container changes between normal/fullscreen.
    requestAnimationFrame(() => {
      window.dispatchEvent(new Event('resize'));
    });
  }, [isExpanded]);

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (
        !(
          (e.code === KeyCodes.KEY_F && e.ctrlKey) ||
          e.key === KeyCodes.KEY_ESCAPE
        )
      ) {
        return;
      }
      e.preventDefault();
      if (e.code === KeyCodes.KEY_F && e.ctrlKey) {
        setIsExpanded(true);
      }
      if (e.key === KeyCodes.KEY_ESCAPE) {
        setIsExpanded(false);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  if (ignoreFullScreen) {
    const noOpFullScreenButton: ReactNode = <></>;
    return (
      <>
        {typeof children === 'function'
          ? children(noOpFullScreenButton, false)
          : children}
      </>
    );
  }

  const fullScreenButton = (
    <Box className={fullScreenBtnClass ?? classes.fsBtnStyles}>
      <IconButton onClick={() => setIsExpanded(!isExpanded)}>
        {!isExpanded && (
          <Tooltip title={t('attributes.fullscreen')}>
            <Fullscreen />
          </Tooltip>
        )}
        {isExpanded && (
          <Tooltip title={t('attributes.exit')}>
            <ExitToApp />
          </Tooltip>
        )}
      </IconButton>
    </Box>
  );

  const content =
    typeof children === 'function' ? (
      children(fullScreenButton, isExpanded)
    ) : (
      <>
        {fullScreenButton}
        {children}
      </>
    );

  return (
    <Grid
      className={
        isExpanded
          ? `${classes.fullScreen} full-screen-container`
          : classes.exitFullScreen
      }
    >
      {content}
    </Grid>
  );
};

export default ToggleFullScreen;
